// Minimal fetch wrapper — mirrors mobile/src/services/api.ts shape.

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:5000/api'

const ADMIN_TOKEN_KEY = 'adminAuthToken'
const AUTH_FAILURE_EVENT = 'admin:auth-failure'

let authToken: string | null = (() => {
  try {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(ADMIN_TOKEN_KEY)
  } catch {
    return null
  }
})()

export function setAuthToken(token: string | null) {
  authToken = token
}

export function subscribeToAuthFailure(
  handler: (args: { status: number; message: string }) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}

  const listener = (e: Event) => {
    const detail = (e as CustomEvent).detail as { status?: number; message?: string } | undefined
    handler({ status: detail?.status ?? 0, message: detail?.message ?? 'Authentication failed' })
  }
  window.addEventListener(AUTH_FAILURE_EVENT, listener)
  return () => window.removeEventListener(AUTH_FAILURE_EVENT, listener)
}

function emitAuthFailure(args: { status: number; message: string }) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_FAILURE_EVENT, { detail: args }))
}

function clearAdminSession() {
  try {
    if (typeof window === 'undefined') return
    localStorage.removeItem(ADMIN_TOKEN_KEY)
  } catch {
    // ignore
  } finally {
    authToken = null
  }
}

export class ApiError extends Error {
  status: number
  data?: unknown
  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.status = status
    this.data = data
    this.name = 'ApiError'
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!isFormData) headers.set('Content-Type', 'application/json')
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`)

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!res.ok) {
    // Backend returns JSON like: { success:false, message:"...", errors?:[], stack?:... }
    // Prefer a clean message over raw JSON string.
    const rawText = await res.text().catch(() => '')
    const fallback = rawText || res.statusText

    let parsed: unknown = null
    if (rawText) {
      try {
        parsed = JSON.parse(rawText) as unknown
      } catch {
        parsed = null
      }
    }

    const record = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
    const message =
      record && typeof record.message === 'string' && record.message.trim()
        ? record.message
        : fallback || res.statusText

    // Note: 403 is intentionally excluded — the backend also uses it for permission
    // grants (e.g. "You are not allowed to create posts") on an otherwise-valid
    // session, which should surface as an inline error, not force a logout.
    const msgLower = message.toLowerCase()
    const isAuthFailure =
      res.status === 401 ||
      (res.status === 404 && (msgLower.includes('user not found') || msgLower.includes('admin not found'))) ||
      msgLower.includes('token expired') ||
      msgLower.includes('invalid token') ||
      msgLower.includes('jwt expired')

    if (isAuthFailure) {
      clearAdminSession()
      emitAuthFailure({ status: res.status, message })
    }

    throw new ApiError(res.status, message, parsed ?? rawText)
  }
  return res.json() as Promise<T>
}
